// ✅ src/pages/Asset/AssetSubTypeMasterListing.jsx
// FULL FILE - API + PAGINATION + DELETE + COLOR SQUARE + LOCALSTORAGE HEADERS
// Mount backend: app.use("/api/subtypeListing", assetSubTypeListing);

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/subtypeListing";

const AssetSubTypeMasterListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // -------------------------
  // LocalStorage values
  // -------------------------
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // -------------------------
  // State
  // -------------------------
  const [assetTypes, setAssetTypes] = useState([{ value: "", label: "Select" }]);
  const [selectedAssetType, setSelectedAssetType] = useState("");

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // -------------------------
  // UI classes
  // -------------------------
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  // -------------------------
  // Color helpers
  // -------------------------
  const normalizeHex = (v) => {
    if (!v) return "";
    let s = String(v).trim();
    if (!s) return "";

    if (s.startsWith("#")) s = s.slice(1);

    if (/^[0-9A-Fa-f]{6}$/.test(s)) return `#${s.toUpperCase()}`;

    return "";
  };

  // -------------------------
  // Normalize rows
  // -------------------------
  const normalizedRows = useMemo(() => {
    return (records || []).map((r) => {
      const id =
        r.fld_AssetSubtypeid ??
        r.fld_AssetSubTypeID ??
        r.fld_assetsubtypeid ??
        r.fld_assetsubtypeID ??
        r.id;

      const rawColor =
        r.fld_ColorCode ??
        r.fld_colorCode ??
        r.fld_colourCode ??
        r.colorCode ??
        "";

      return {
        id: id,
        assetSubTypeName:
          r.fld_assetsubtypename ??
          r.fld_AssetSubTypeName ??
          r.assetSubTypeName ??
          "",
        assetSubTypeCode:
          r.fld_assetsubtypecode ??
          r.fld_AssetSubTypeCode ??
          r.assetSubTypeCode ??
          "",
        status: r.status ?? r.fld_status ?? r.fld_Status ?? "Active",
        colorCode: normalizeHex(rawColor),
        assetTypeName:
          r.assetTypeName ??
          r.fld_AssetTypeName ??
          r.fld_assettypename ??
          "",
      };
    });
  }, [records]);

  // -------------------------
  // API: Load dropdown (Asset Types)
  // -------------------------
  const loadAssetTypes = async () => {
    try {
      const rs = await axios.get(`${API_BASE}/assetTypes`, {
        headers: commonHeaders,
      });

      const rows = rs.data?.data || [];

      const mapped = rows.map((x) => ({
        value:
          x.value ??
          x.fld_AssetTypeID ??
          x.fld_assettypeid ??
          x.fld_AssetTypeName ??
          x.fld_assettypename ??
          x.fld_AssetTypeCode ??
          x.fld_assettypecode ??
          "",
        label:
          x.label ??
          x.AssetType ??
          x.fld_AssetTypeName ??
          x.fld_assettypename ??
          x.fld_AssetTypeCode ??
          x.fld_assettypecode ??
          String(x.fld_AssetTypeID ?? x.fld_assettypeid ?? x.value ?? ""),
      }));

      setAssetTypes([{ value: "", label: "Select" }, ...mapped]);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load Asset Types");
    }
  };

  useEffect(() => {
    loadAssetTypes();
    handleSearch(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------
  // API: Fetch list
  // -------------------------
  const fetchList = async ({ assetTypeValue, pageNo }) => {
    setLoading(true);
    try {
      const rs = await axios.get(`${API_BASE}/list`, {
        headers: commonHeaders,
        params: {
          assetType: assetTypeValue || "",
          page: pageNo,
          pageSize,
        },
      });

      setRecords(rs.data?.data || []);
      setTotalPages(rs.data?.totalPages || 1);
      setTotalCount(rs.data?.totalCount || 0);
      setPage(rs.data?.page || pageNo);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load listing");
      setRecords([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Search
  // -------------------------
  const handleSearch = async (pageNo = 1, silent = false) => {
    await fetchList({ assetTypeValue: selectedAssetType, pageNo });
    if (!silent) toast.success("Loaded records");
  };

  // -------------------------
  // Pagination
  // -------------------------
  const handlePageChange = async (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    await fetchList({ assetTypeValue: selectedAssetType, pageNo: p });
  };

  // -------------------------
  // Actions
  // -------------------------
  const handleAddNew = () => {
    navigate("/Asset/AssetSubTypeMaster");
  };

  const handleEdit = (row) => {
    if (!row?.id) return toast.error("Invalid ID");
    navigate(`/Asset/AssetSubTypeMaster?id=${row.id}`);
  };

  const handleDelete = async (row) => {
    if (!row?.id) return toast.error("Invalid ID");
    const ok = window.confirm(`Delete "${row.assetSubTypeName}" ?`);
    if (!ok) return;

    try {
      await axios.delete(`${API_BASE}/delete/${row.id}`, {
        headers: commonHeaders,
      });

      toast.success("Deleted successfully");

      const newCount = totalCount - 1;
      const newTotalPages = Math.max(Math.ceil(newCount / pageSize), 1);
      const nextPage = Math.min(page, newTotalPages);

      await fetchList({ assetTypeValue: selectedAssetType, pageNo: nextPage });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET SUB TYPE MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Asset Sub Type Master Listing
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalCount} Records
              </span>
            </div>

            <button
              onClick={handleAddNew}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPlus />
              Add New
            </button>
          </div>

          <div
            className={`rounded-xl border mb-6 p-6 ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-4">
                <label className={`${labelClass} mb-0 whitespace-nowrap`}>Asset Type</label>
                <select
                  value={selectedAssetType}
                  onChange={(e) => setSelectedAssetType(e.target.value)}
                  className={`${inputClass} min-w-64`}
                >
                  {assetTypes.map((t) => (
                    <option key={`${t.value}-${t.label}`} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => handleSearch(1)}
                disabled={loading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSearch />
                {loading ? "Loading..." : "Search"}
              </button>
            </div>

            <div className={`text-center mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Tip: If you keep Asset Type as <b>Select</b> and click Search, it will show <b>all</b> records.
            </div>
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              {loading ? (
                <div className={`p-8 text-center ${isDark ? "bg-gray-900 text-gray-300" : "bg-white text-gray-600"}`}>
                  Loading...
                </div>
              ) : normalizedRows.length === 0 ? (
                <div className={`p-8 text-center ${isDark ? "bg-gray-900 text-gray-400" : "bg-white text-gray-500"}`}>
                  <div className="text-lg font-medium mb-2">No Records Found!</div>
                  <p className="text-sm">Click Search to load records</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">Sl No</th>
                      <th className="px-4 py-4 text-center font-semibold">Asset Sub Type Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Asset Sub Type Code</th>
                      <th className="px-4 py-4 text-center font-semibold">Color</th>
                      <th className="px-4 py-4 text-center font-semibold">Status</th>
                      <th className="px-4 py-4 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>

                  <tbody className={isDark ? "bg-gray-900" : "bg-white"}>
                    {normalizedRows.map((row, idx) => {
                      const hasHex = !!row.colorCode;

                      return (
                        <tr
                          key={row.id ?? idx}
                          className={`border-t ${isDark ? "border-gray-800" : "border-gray-200"} ${
                            isDark ? "hover:bg-white/5" : "hover:bg-gray-50"
                          }`}
                        >
                          <td className={`px-4 py-4 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-center text-sm font-semibold ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                            {row.assetSubTypeName}
                          </td>

                          <td className={`px-4 py-4 text-center text-sm ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                            {row.assetSubTypeCode}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div
                                className={`w-5 h-5 rounded border ${isDark ? "border-gray-600" : "border-gray-300"}`}
                                style={{ backgroundColor: hasHex ? row.colorCode : "transparent" }}
                                title={hasHex ? row.colorCode : "No Color"}
                              />
                              <span className={`text-sm ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                                {hasHex ? row.colorCode : "-"}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                String(row.status).toLowerCase().includes("active")
                                  ? isDark
                                    ? "bg-green-900 text-green-100"
                                    : "bg-green-100 text-green-800"
                                  : isDark
                                  ? "bg-red-900 text-red-100"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {row.status}
                            </span>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => handleEdit(row)}
                                className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200"
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handleDelete(row)}
                                className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all duration-200"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-start gap-2 mt-6 flex-wrap">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || loading}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  page <= 1 || loading
                    ? "opacity-50 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                title="Previous"
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  disabled={loading}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === p
                      ? "bg-blue-600 text-white border-blue-600"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {p}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || loading}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  page >= totalPages || loading
                    ? "opacity-50 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                title="Next"
              >
                <FaChevronRight />
              </button>

              <div className={`ml-2 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Page <b>{page}</b> / <b>{totalPages}</b>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetSubTypeMasterListing;